import * as XLSX from 'xlsx';

const WeeklySalesExport = {
  exportToExcel: async (salesData, filters, searchTerm = '') => {
    try {
      if (!salesData || !salesData.data) {
        alert('No data to export');
        return;
      }

      // Prepare the workbook
      const workbook = XLSX.utils.book_new();
      
      // Get weeks info
      const weeks = Object.keys(salesData.weekInfo || {});
      const weekInfo = salesData.weekInfo || {};
      
      // Filter data based on search term if provided
      let dataEntries = Object.entries(salesData.data);
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        dataEntries = dataEntries.filter(([sku]) => 
          sku.toLowerCase().includes(searchLower)
        );
      }

      // Sort by total sales (descending)
      const sortedData = dataEntries.sort((a, b) => {
        const totalA = Object.values(a[1]).reduce((sum, val) => 
          typeof val === 'number' ? sum + val : sum, 0) - (a[1].mtd || 0);
        const totalB = Object.values(b[1]).reduce((sum, val) => 
          typeof val === 'number' ? sum + val : sum, 0) - (b[1].mtd || 0);
        return totalB - totalA;
      });

      // Main data worksheet
      const mainData = [];
      
      // Header row
      const headers = ['SKU'];
      weeks.forEach(weekKey => {
        const weekData = weekInfo[weekKey];
        headers.push(`Week ${weekKey.replace('week', '')} (${weekData?.start || ''} - ${weekData?.end || ''})`);
      });
      headers.push('Total');
      mainData.push(headers);

      // Data rows
      sortedData.forEach(([sku, values]) => {
        const row = [sku];
        
        // Add weekly values
        weeks.forEach(weekKey => {
          row.push(values[weekKey] || 0);
        });
        
        // Add total
        const total = Object.values(values).reduce((sum, val) => 
          typeof val === 'number' ? sum + val : sum, 0) - (values.mtd || 0);
        row.push(total);
        
        mainData.push(row);
      });

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(mainData);
      
      // Set column widths
      const columnWidths = [
        { wch: 15 }, // SKU
        ...weeks.map(() => ({ wch: 18 })), // Week columns
        { wch: 12 } // Total
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Weekly Sales');

      // Summary worksheet
      const summaryData = [];
      
      // Summary headers
      summaryData.push(['Weekly Sales Summary']);
      summaryData.push(['']);
      
      // Report period
      if (salesData.reportPeriod) {
        summaryData.push(['Report Period:', `${salesData.reportPeriod.start} to ${salesData.reportPeriod.end}`]);
      }
      
      // Filter information
      summaryData.push(['']);
      summaryData.push(['Applied Filters:']);
      
      if (filters.regionConfigs && filters.regionConfigs.length > 0) {
        summaryData.push(['Region Configs:', filters.regionConfigs.join(', ')]);
      }
      
      if (filters.categoryNames && filters.categoryNames.length > 0) {
        summaryData.push(['Categories:', filters.categoryNames.join(', ')]);
      }
      
      if (filters.productNames && filters.productNames.length > 0) {
        summaryData.push(['Products:', filters.productNames.join(', ')]);
      }
      
      if (filters.startDate && filters.endDate) {
        summaryData.push(['Date Range:', `${filters.startDate.toLocaleDateString()} to ${filters.endDate.toLocaleDateString()}`]);
      }
      
      if (searchTerm) {
        summaryData.push(['Search Term:', searchTerm]);
      }
      
      summaryData.push(['']);
      summaryData.push(['Summary Statistics:']);
      summaryData.push(['Total SKUs:', sortedData.length]);
      
      // Calculate totals per week
      const weekTotals = {};
      let grandTotal = 0;
      
      weeks.forEach(weekKey => {
        weekTotals[weekKey] = sortedData.reduce((sum, [sku, values]) => 
          sum + (values[weekKey] || 0), 0);
      });
      
      grandTotal = sortedData.reduce((sum, [sku, values]) => {
        const total = Object.values(values).reduce((sum, val) => 
          typeof val === 'number' ? sum + val : sum, 0) - (values.mtd || 0);
        return sum + total;
      }, 0);
      
      summaryData.push(['']);
      summaryData.push(['Weekly Totals:']);
      weeks.forEach(weekKey => {
        const weekData = weekInfo[weekKey];
        summaryData.push([
          `Week ${weekKey.replace('week', '')} (${weekData?.start || ''} - ${weekData?.end || ''})`, 
          weekTotals[weekKey]
        ]);
      });
      summaryData.push(['Grand Total:', grandTotal]);

      // Create summary worksheet
      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Set summary column widths
      summaryWorksheet['!cols'] = [
        { wch: 25 }, // Labels
        { wch: 30 }  // Values
      ];

      // Add summary to workbook
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const regionStr = filters.regionConfigs?.length > 0 ? `_${filters.regionConfigs.join('-')}` : '';
      const filterStr = (filters.categoryNames?.length > 0 || filters.productNames?.length > 0 || searchTerm) ? '_filtered' : '';
      const filename = `Weekly_Sales_${dateStr}${regionStr}${filterStr}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);
      
      return true;
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
      return false;
    }
  }
};

export default WeeklySalesExport; 
